import React from 'react'
import classNames from 'classnames'

import { Button, Text } from 'components/base'
import {
  MailIcon,
  ShareIcon,
  InfoIcon,
  RecentIcon,
  SparklesIcon,
  RecurringIcon,
} from 'components/Utils/Icons'

import {
  TeamSettings,
  ManageChannelsPermissions,
  ManageWhoCanInvite,
  CreateUserGroups,
  ManageDefaultRole,
  googleSSO,
  AutomaticBackup,
  DisplayNotesActivity,
  DisplayNotesHistory,
  ManagePublicChannels,
  ManagePublicNotes,
  NotesLimit,
} from './constants'

type LayoutWrapperProps = {
  className: string
  onClick: () => void
  buttonLabel?: string
  children: React.ReactNode
}

const LayoutWrapper = ({
  className,
  onClick,
  buttonLabel,
  children,
}: LayoutWrapperProps) => (
  <div
    className={classNames(
      !buttonLabel && 'pointer',
      'radius-normal background-purple-shade-dark padding-larger',
      className
    )}
    onClick={!buttonLabel ? onClick : undefined}
  >
    {children}
    {buttonLabel && (
      <Button.Primary className="margin-top-large" onClick={onClick}>
        {buttonLabel}
      </Button.Primary>
    )}
  </div>
)

type InlineLayoutProps = {
  className: string
  onClick: () => void
  children: React.ReactNode
}

const InlineLayout = ({ className, onClick, children }: InlineLayoutProps) => (
  <div
    className={classNames(
      'pointer',
      'radius-normal background-purple-shade-dark',
      className
    )}
    onClick={onClick}
  >
    {children}
  </div>
)

type IconProps = {
  size: number
  className: string
}

type IconSectionProps = {
  Icon: React.ComponentType<IconProps>
  title: string
  description?: string
  className?: string
}

const IconSection = ({
  Icon,
  title,
  description,
  className,
}: IconSectionProps) => (
  <div className={className}>
    <div className="flexbox margin-bottom-smaller">
      <Icon
        size={12}
        className="primary margin-right-normal margin-top-smaller flexbox-no-shrink"
      />
      <Text.Section>{title}</Text.Section>
    </div>
    {description && (
      <Text.Corpus className="margin-left-large">{description}</Text.Corpus>
    )}
  </div>
)

export type IncentiveBoxLayoutProps = {
  incentiveId: string
  isOrganizationFree?: boolean
  goToOrganizationUpgrade: ({ incentiveId }: { incentiveId: string }) => void
  title?: string
  description?: string
  extraItems?: number
  className?: string
  buttonLabel?: string
  children?: React.ReactNode
}

export function IncentiveBoxLayout({
  incentiveId,
  isOrganizationFree = true,
  goToOrganizationUpgrade,
  title,
  description,
  extraItems,
  className = '',
  buttonLabel,
  children,
}: IncentiveBoxLayoutProps) {
  const onClick = React.useCallback(
    () => {
      goToOrganizationUpgrade({ incentiveId })
    },
    [goToOrganizationUpgrade, incentiveId]
  )

  if (!isOrganizationFree) {
    return null
  }

  switch (incentiveId) {
    case TeamSettings:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade plan"
          onClick={onClick}
        >
          <IconSection
            Icon={MailIcon}
            className="margin-bottom-medium"
            title="Manage Invitations"
            description="Currently any member can invite. Upgrade to prevent some users from inviting."
          />
          <IconSection
            Icon={ShareIcon}
            className="margin-bottom-medium"
            title="Manage sharing permissions"
            description="Currently all members can enable channel and doc public sharing. Upgrade to protect your content."
          />
        </LayoutWrapper>
      )
    case ManageChannelsPermissions:
      return (
        <InlineLayout
          className={classNames(
            className,
            'flexbox flexbox-align-center background-shade-100'
          )}
          onClick={onClick}
        >
          <Text.Callout className="radius-normal primary background-purple-shade-dark padding-horizontal-small padding-vertical-smaller background-primary-shade-dark-on-hover">
            Upgrade
          </Text.Callout>
        </InlineLayout>
      )

    case ManageDefaultRole:
      return (
        <InlineLayout
          className={classNames(
            className,
            'flexbox flexbox-align-center background-shade-100'
          )}
          onClick={onClick}
        >
          <Text.Callout className="radius-normal primary background-purple-shade-dark padding-horizontal-small padding-vertical-smaller background-primary-shade-dark-on-hover">
            Upgrade
          </Text.Callout>
        </InlineLayout>
      )

    case CreateUserGroups:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade to enable groups"
          onClick={onClick}
        >
          <div className="bold margin-bottom-normal">
            User groups let you manage your team’s access rights in bulk.
          </div>
        </LayoutWrapper>
      )
    case ManageWhoCanInvite:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade to manage who can invite"
          onClick={onClick}
        >
          <IconSection
            Icon={MailIcon}
            title="Manage Invitations"
            description="Currently any member can invite. Upgrade to prevent some users from inviting."
          />
        </LayoutWrapper>
      )
    case googleSSO:
      return (
        <InlineLayout
          className={classNames(
            className,
            'flexbox flexbox-align-center padding-normal'
          )}
          onClick={onClick}
        >
          <InfoIcon className="margin-right-small primary" />
          <Text.Corpus>Upgrade to enable Google SSO</Text.Corpus>
        </InlineLayout>
      )
    case DisplayNotesActivity:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade for the full log"
          onClick={onClick}
        >
          <IconSection
            Icon={InfoIcon}
            title={
              extraItems && extraItems > 0
                ? `${extraItems + 1} more activit${
                    extraItems > 1 ? 'ies' : 'y'
                  }`
                : 'Under free plan you can access the 3 last activities'
            }
            description="Acess all the activity log elements, upgrade to the Standard plan."
          />
        </LayoutWrapper>
      )

    case DisplayNotesHistory:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade for full history"
          onClick={onClick}
        >
          <IconSection
            Icon={RecentIcon}
            title={
              extraItems && extraItems > 0
                ? `${extraItems + 1} more version${extraItems > 1 ? 's' : ''}`
                : 'Under free plan you can access the 3 last versions'
            }
            description="Access all versions of this doc, upgrade to the Standard plan."
          />
        </LayoutWrapper>
      )
    case ManagePublicNotes:
      return (
        <InlineLayout
          className={classNames(
            className,
            'flexbox flexbox-align-center padding-normal'
          )}
          onClick={onClick}
        >
          <InfoIcon className="margin-right-small primary" />
          <Text.Corpus>
            Upgrade to manage who can share docs publicly
          </Text.Corpus>
        </InlineLayout>
      )
    case ManagePublicChannels:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade"
          onClick={onClick}
        >
          <IconSection
            Icon={SparklesIcon}
            title="Manage public sharing"
            description="Manage which members can share publicly and remove Slite branding by upgrading to the Standard plan."
          />
        </LayoutWrapper>
      )
    case AutomaticBackup:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel="Upgrade plan"
          onClick={onClick}
        >
          <IconSection
            Icon={RecurringIcon}
            title="Automatic backup"
            description="Under the standard plan, schedule regular backups of your team’s content."
          />
        </LayoutWrapper>
      )
    case NotesLimit:
      return (
        <div
          className={classNames(
            'pointer',
            'radius-normal background-purple-shade-dark padding-normal',
            className
          )}
          onClick={onClick}
        >
          <IconSection
            Icon={SparklesIcon}
            title={title || 'Upgrade to keep writing'}
            description={description}
          />
        </div>
      )

    default:
      return (
        <LayoutWrapper
          className={className}
          buttonLabel={buttonLabel}
          onClick={onClick}
        >
          {children}
        </LayoutWrapper>
      )
  }
}
